import { notify } from '@billy/design-system'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from 'react-query'

import { submitLifecycleAction, type SubmitLifecycleActionProps } from '@features/attachments/query-api'

import { NotificationKeys } from '../../../enums/notificationKeys'
import { QueryKeys } from '../../../enums/queryKeys'
import type { APIError } from '../../../utils'
import type { EInvoiceLifecycleAction } from '../types/eInvoiceLifecycleAction'

const SUCCESS_MESSAGE_KEYS: Record<EInvoiceLifecycleAction, string> = {
  approve: 'receipt.approved.e_invoice',
  refuse: 'receipt.rejected.e_invoice',
  dispute: 'receipt.disputed.e_invoice',
  suspend: 'receipt.suspended.e_invoice',
}

const ERROR_MESSAGE_KEYS: Record<EInvoiceLifecycleAction, string> = {
  approve: 'receipt.approved.e_invoice.error',
  refuse: 'receipt.rejected.e_invoice.error',
  dispute: 'receipt.disputed.e_invoice.error',
  suspend: 'receipt.suspended.e_invoice.error',
}

export type LifecycleActionPayload = Omit<SubmitLifecycleActionProps, 'action'>

interface UseLifecycleActionProps {
  action: EInvoiceLifecycleAction
  onError?: (error?: APIError) => void
  onSuccess?: (payload: LifecycleActionPayload) => void
}

export const useLifecycleAction = ({ action, onError, onSuccess }: UseLifecycleActionProps) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const {
    mutate: submit,
    isLoading: isProcessing,
    ...rest
  } = useMutation((payload: LifecycleActionPayload) => submitLifecycleAction({ ...payload, action }), {
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries(QueryKeys.AttachmentsList)
      queryClient.invalidateQueries(QueryKeys.AttachmentsCount)
      notify({
        id: NotificationKeys.ReceiptsList,
        message: t(SUCCESS_MESSAGE_KEYS[action]),
        variant: 'success',
      })
      onSuccess?.(variables)
    },
    onError: () => {
      notify({
        id: NotificationKeys.ReceiptsList,
        message: t(ERROR_MESSAGE_KEYS[action]),
        variant: 'error',
      })
      onError?.()
    },
  })

  return { submit, isProcessing, ...rest }
}
